import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { authConfig } from './auth.config';

type MeProfile = {
  id?: string;
  profileId?: string;
  memberId?: string;
  memberiId?: string;
  benefitStatus?: 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'SUSPENDED';
  profileImageUrl?: string;
  profileImageThumbUrl?: string;
};

type MeResponse = {
  profile?: MeProfile;
};

type MeSessionSnapshot = {
  profileId: string;
  matricule: string;
  benefitStatus: 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'SUSPENDED';
  profileImageUrl: string;
  profileImageThumbUrl: string;
};

@Injectable({ providedIn: 'root' })
export class MeSessionService {
  private readonly http = inject(HttpClient);
  private readonly storageKey = 'agora.me.session';

  private readonly loadedState = signal(false);
  private readonly loadingState = signal(false);
  private readonly lastErrorState = signal<string | null>(null);

  private readonly profileIdState = signal('');
  private readonly matriculeState = signal('-');
  private readonly benefitStatusState = signal<'ELIGIBLE' | 'NOT_ELIGIBLE' | 'SUSPENDED'>('NOT_ELIGIBLE');
  private readonly profileImageUrlState = signal('');
  private readonly profileImageThumbUrlState = signal('');

  constructor() {
    this.hydrateFromSessionStorage();
  }

  readonly loaded = computed(() => this.loadedState());
  readonly loading = computed(() => this.loadingState());
  readonly lastError = computed(() => this.lastErrorState());
  readonly profileId = computed(() => this.profileIdState());
  readonly matricule = computed(() => this.matriculeState());
  readonly benefitStatus = computed(() => this.benefitStatusState());
  readonly profileImageUrl = computed(() => this.profileImageUrlState());
  readonly profileImageThumbUrl = computed(() => this.profileImageThumbUrlState());

  refresh(): void {
    if (this.loadingState()) {
      return;
    }

    this.loadingState.set(true);
    this.lastErrorState.set(null);

    this.http.get<MeResponse>(`${authConfig.apiBaseUrl}/api/v1/me`).subscribe({
      next: (response) => {
        const normalized = this.normalizeResponse(response);
        this.applySnapshot(normalized);
        this.persistToSessionStorage(normalized);
        this.loadedState.set(true);
        this.loadingState.set(false);
      },
      error: () => {
        this.lastErrorState.set('Impossible de charger le profil de session.');
        this.loadingState.set(false);
      }
    });
  }

  clear(): void {
    this.loadedState.set(false);
    this.loadingState.set(false);
    this.lastErrorState.set(null);
    this.profileIdState.set('');
    this.matriculeState.set('-');
    this.benefitStatusState.set('NOT_ELIGIBLE');
    this.profileImageUrlState.set('');
    this.profileImageThumbUrlState.set('');
    sessionStorage.removeItem(this.storageKey);
  }

  private normalizeResponse(response: MeResponse | null | undefined): MeSessionSnapshot {
    const profile = response?.profile;
    const profileId = this.normalizeString(profile?.profileId) || this.normalizeString(profile?.id);
    const matricule = this.normalizeString(profile?.memberiId) || this.normalizeString(profile?.memberId) || '-';
    const benefitStatus = profile?.benefitStatus === 'ELIGIBLE' || profile?.benefitStatus === 'SUSPENDED'
      ? profile.benefitStatus
      : 'NOT_ELIGIBLE';
    const profileImageUrl = this.normalizeString(profile?.profileImageUrl);
    const profileImageThumbUrl = this.normalizeString(profile?.profileImageThumbUrl);

    return {
      profileId,
      matricule,
      benefitStatus,
      profileImageUrl,
      profileImageThumbUrl
    };
  }

  private applySnapshot(snapshot: MeSessionSnapshot): void {
    this.profileIdState.set(snapshot.profileId);
    this.matriculeState.set(snapshot.matricule || '-');
    this.benefitStatusState.set(snapshot.benefitStatus);
    this.profileImageUrlState.set(snapshot.profileImageUrl);
    this.profileImageThumbUrlState.set(snapshot.profileImageThumbUrl);
  }

  private hydrateFromSessionStorage(): void {
    const raw = sessionStorage.getItem(this.storageKey);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<MeSessionSnapshot>;
      const hydrated: MeSessionSnapshot = {
        profileId: this.normalizeString(parsed.profileId),
        matricule: this.normalizeString(parsed.matricule) || '-',
        benefitStatus: parsed.benefitStatus === 'ELIGIBLE' || parsed.benefitStatus === 'SUSPENDED'
          ? parsed.benefitStatus
          : 'NOT_ELIGIBLE',
        profileImageUrl: this.normalizeString(parsed.profileImageUrl),
        profileImageThumbUrl: this.normalizeString(parsed.profileImageThumbUrl)
      };

      this.applySnapshot(hydrated);
      this.loadedState.set(true);
    } catch {
      sessionStorage.removeItem(this.storageKey);
    }
  }

  private persistToSessionStorage(snapshot: MeSessionSnapshot): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(snapshot));
  }

  private normalizeString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
